/**
 * CustomItemModal (m-custom) — Create a custom line item with editable cost + measure.
 *
 * M-6: Custom items unlock cost/measure fields; visually separated from catalog items.
 */

import React, { useState } from 'react';
import Slideover from '../../../ui/Slideover';
import { Button } from '../../../ui/Button';
import { Input } from '../../../ui/Input';
import SelectMenu from '../../../ui/SelectMenu';
import { AREAS, getSubAreas, buildCustomLineItem } from '../../../../quote-builder/seedCatalog';
import { parseNullableMarginPct } from '../../../../quote-builder/marginEngine';

export default function CustomItemModal({ open, onClose, onAdd, defaultAreaId }) {
  const [areaId, setAreaId] = useState(defaultAreaId || '');
  const [subAreaId, setSubAreaId] = useState('');
  const [description, setDescription] = useState('');
  const [materialCost, setMaterialCost] = useState('');
  const [laborCost, setLaborCost] = useState('');
  const [margin, setMargin] = useState('35');

  const subAreas = getSubAreas(areaId);

  const handleSubmit = () => {
    if (!description.trim()) return;
    const item = buildCustomLineItem({
      areaId,
      subAreaId,
      description: description.trim(),
      materialCost: Math.round(parseFloat(materialCost || 0) * 100),
      laborCost: Math.round(parseFloat(laborCost || 0) * 100),
      margin: parseNullableMarginPct(margin),
    });
    onAdd(item);
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setDescription('');
    setMaterialCost('');
    setLaborCost('');
    setMargin('35');
    setSubAreaId('');
  };

  const footer = (
    <div className="flex gap-2 justify-end w-full">
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button onClick={handleSubmit} disabled={!description.trim()}>Add Custom Item</Button>
    </div>
  );

  return (
    <Slideover open={open} onClose={onClose} title="Custom Item" subtitle="Editable cost and measure fields" footer={footer} width="sm">
      <div className="space-y-4">
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the custom item…"
          required
        />

        <div>
          <label className="label mb-1">Area</label>
          <SelectMenu
            value={areaId}
            onChange={(val) => { setAreaId(val); setSubAreaId(''); }}
            options={AREAS.map((a) => ({ value: a.id, label: a.label }))}
            placeholder="Select area…"
          />
        </div>

        {subAreas.length > 0 && (
          <div>
            <label className="label mb-1">Sub-area</label>
            <SelectMenu
              value={subAreaId}
              onChange={setSubAreaId}
              options={subAreas.map((sa) => ({ value: sa.id, label: sa.label }))}
              placeholder="Select sub-area…"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Material Cost ($)"
            type="number"
            min="0"
            step="0.01"
            value={materialCost}
            onChange={(e) => setMaterialCost(e.target.value)}
            placeholder="0.00"
          />
          <Input
            label="Labor Cost ($)"
            type="number"
            min="0"
            step="0.01"
            value={laborCost}
            onChange={(e) => setLaborCost(e.target.value)}
            placeholder="0.00"
          />
        </div>

        <Input
          label="Margin %"
          type="number"
          min="0"
          max="99"
          value={margin}
          onChange={(e) => setMargin(e.target.value)}
          placeholder="35"
        />

        {/* Cost preview */}
        {(materialCost || laborCost) && (
          <div className="card p-3 text-sm" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--neutral-50)' }}>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Total cost: ${(parseFloat(materialCost || 0) + parseFloat(laborCost || 0)).toFixed(2)}
            </p>
            {margin && (
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Estimated total: ${((parseFloat(materialCost || 0) + parseFloat(laborCost || 0)) / (1 - parseFloat(margin) / 100)).toFixed(2)}
              </p>
            )}
          </div>
        )}
      </div>
    </Slideover>
  );
}
